import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Checkbox,
  FormControlLabel,
  Switch,
  FormGroup,
  IconButton,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  CloudUpload as PushIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { request } from '../common/services/request'
import { formatLocalDateTime } from '../common/utils'
import { API_ENDPOINTS } from '../common/endpoints'
import VitalsChart from '../components/VitalsChart'
import type {
  Patient,
  VitalRecord,
  VitalRecordListResponse,
  DiseaseProfile,
  GenerateVitalsRequest,
  GenerationJob,
  ScheduleCreate,
  MetricType,
  Schedule,
} from '../common/types'
import { DISEASE_PROFILES, METRIC_TYPES } from '../common/types'

function PatientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [hasEncounter, setHasEncounter] = useState(false)
  const [encounters, setEncounters] = useState<any[]>([])
  const [patient, setPatient] = useState<Patient | null>(null)
  const [vitals, setVitals] = useState<VitalRecord[]>([])
  const [vitalsTotal, setVitalsTotal] = useState(0)
  const [vitalsPage, setVitalsPage] = useState(0)
  const [vitalsPageSize, setVitalsPageSize] = useState(20)
  const [filterMetric, setFilterMetric] = useState<MetricType>('sbp')
  const [nextRunAt, setNextRunAt] = useState<string | null>(null)
  const [patientSchedules, setPatientSchedules] = useState<Schedule[]>([])
  const [loadingSchedules, setLoadingSchedules] = useState(false)

  const formatPhone = (phone: string | null): string => {
    if (!phone) return '-'
    const extMatch = phone.match(/(?:ext\.?|x)\s*(\d+)$/i)
    const ext = extMatch ? extMatch[1] : null
    const digits = phone.replace(/[^0-9]/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
      const area = digits.slice(1, 4)
      const p1 = digits.slice(4, 7)
      const p2 = digits.slice(7)
      return `(+1) ${area}-${p1}-${p2}${ext ? ` x${ext}` : ''}`
    }
    if (digits.length === 10) {
      const area = digits.slice(0, 3)
      const p1 = digits.slice(3, 6)
      const p2 = digits.slice(6)
      return `(${area}) ${p1}-${p2}${ext ? ` x${ext}` : ''}`
    }
    if (digits.length > 4) {
      const last = digits.slice(-4)
      const rest = digits.slice(0, -4)
      return `${rest}-${last}${ext ? ` x${ext}` : ''}`
    }
    return phone
  }

  // Schedule creation dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [scheduleCustomNoise, setScheduleCustomNoise] = useState(false)
  const [scheduleCustomDropout, setScheduleCustomDropout] = useState(false)
  const [scheduleUrlError, setScheduleUrlError] = useState<string | null>(null)
  const [scheduleForm, setScheduleForm] = useState<ScheduleCreate>({
    openemr_patient_uuid: id || '',
    name: '',
    interval: '6h',
    window_days: 7,
    profile: 'normal',
    metrics: ['hr', 'sbp', 'dbp'],
    seed: 42,
    dropout_rate: 0.0,
    noise_level: 0.1,
    push_to_openemr: true,
    destination_url: '',
  })

  const fetchPatient = async () => {
    try {
      const client = request()
      const res = await client.get<Patient>(API_ENDPOINTS.PATIENTS.BY_ID(id!))
      setPatient(res.data)
    } catch (e: any) {
      setError(e.message || 'Failed to load patient')
    }
  }

  const fetchEncounters = async () => {
    try {
      const client = request()
      const res = await client.get(API_ENDPOINTS.OPENEMR.PATIENT_ENCOUNTERS(id!))
      const items = res.data?.data || []
      setHasEncounter((items?.length ?? 0) > 0)
      setEncounters(items)
    } catch (e) {
      console.debug('Failed to load encounters', e)
      setHasEncounter(false)
      setEncounters([])
    }
  }


  const fetchVitals = async () => {
    try {
      const client = request()
      const res = await client.get<VitalRecordListResponse>(API_ENDPOINTS.VITALS.LIST(id!), {
        params: { page: vitalsPage + 1, page_size: vitalsPageSize },
      })
      setVitals(res.data.items)
      setVitalsTotal(res.data.total)
    } catch (e: any) {
      console.error('Failed to load vitals', e)
    }
  }

  const handleDeleteVital = async (vitalId: number) => {
    if (!confirm('Delete this vital? This action cannot be undone.')) return
    try {
      const client = request()
      await client.delete(API_ENDPOINTS.VITALS.BY_ID(id!, vitalId))
      await fetchVitals()
      setSuccess('Vital deleted')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete vital')
    }
  }

  const handleClearAllVitals = async () => {
    if (!confirm('Delete ALL vitals for this patient? This cannot be undone.')) return
    try {
      const client = request()
      // keep deleting page 1 until empty to avoid skipping due to paging changes
      const pageSize = 100
      while (true) {
        const res = await client.get(API_ENDPOINTS.VITALS.LIST(id!), { params: { page: 1, page_size: pageSize } })
        const items: any[] = res.data.items || []
        if ((items?.length ?? 0) === 0) break
        for (const v of items) {
          try {
            await client.delete(API_ENDPOINTS.VITALS.BY_ID(id!, v.id))
          } catch (e) {
            console.debug('Failed to delete vital', v.id, e)
          }
        }
        // loop again to fetch remaining items starting from page 1
      }
      await fetchVitals()
      setSuccess('All vitals deleted')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to clear vitals')
    }
  }

  const openForwardDialog = (vitalId: number) => {
    setForwardVitalId(vitalId)
    setForwardUrl('')
    setForwardDialogOpen(true)
  }

  const handleForwardVital = async () => {
    if (!forwardVitalId) return
    if (!forwardUrl) {
      setError('Forwarding URL is required')
      return
    }
    try {
      const client = request()
      await client.post(API_ENDPOINTS.VITALS.PUSH(id!), {
        vital_ids: [forwardVitalId],
        destination_url: forwardUrl,
      })
      setSuccess('Vital forwarded successfully')
      setForwardDialogOpen(false)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to forward vital')
    }
  }

  const fetchSchedulesForPatient = async () => {
    try {
      setLoadingSchedules(true)
      const client = request()
      const res = await client.get(API_ENDPOINTS.SCHEDULES.BASE, {
        params: { openemr_patient_uuid: id, page: 1, page_size: 50 },
      })
      const items = res.data.items || []
      setPatientSchedules(items)
      // find earliest next_run_at among active schedules
      const activeNext = items
        .filter((s: any) => s.next_run_at)
        .map((s: any) => s.next_run_at)
        .sort()
      setNextRunAt((activeNext?.length ?? 0) > 0 ? activeNext[0] : null)
    } catch (e) {
      console.debug('Failed to load schedules for patient', e)
      setPatientSchedules([])
    } finally {
      setLoadingSchedules(false)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await fetchPatient()
      await fetchVitals()
      await fetchSchedulesForPatient()
      await fetchEncounters()
      setLoading(false)
    }
    loadData()
  }, [id])

  useEffect(() => {
    fetchVitals()
  }, [vitalsPage, vitalsPageSize])

  useEffect(() => {
    // refresh encounters when patient changes
    fetchEncounters()
  }, [])

  useEffect(() => {
    // refresh next run when schedules might change
    fetchSchedulesForPatient()
  }, [])
  const handleCreateSchedule = async () => {
    // Validate URL
    const url = scheduleForm.destination_url.trim()
    if (!url) {
      setScheduleUrlError('Destination URL is required')
      return
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setScheduleUrlError('URL must start with http:// or https://')
      return
    }
    
    try {
      const client = request()
      const payload = { 
        ...scheduleForm, 
        destination_url: url,
        noise_level: clamp(Number(scheduleForm.noise_level) || 0.1) 
      }
      await client.post(API_ENDPOINTS.SCHEDULES.BASE, payload)
      setSuccess('Schedule created successfully')
      setScheduleDialogOpen(false)
      setScheduleUrlError(null)
      fetchSchedulesForPatient()
      // Reset form
      setScheduleForm({
        openemr_patient_uuid: id || '',
        name: '',
        interval: '6h',
        profile: 'normal',
        metrics: ['hr', 'sbp', 'dbp'],
        seed: 42,
        noise_level: 0.1,
        dropout_rate: 0.0,
        push_to_openemr: true,
        destination_url: '',
      })
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create schedule')
    }
  }

  const handlePushVitals = async () => {
    try {
      const client = request()
      
      // Use first available encounter if exists
      let encounter_eid: string | undefined
      if ((encounters?.length ?? 0) > 0) {
        const first = encounters[0]
        encounter_eid = first?.euuid || first?.uuid || (first?.eid ? String(first.eid) : undefined)
      }
      
      const res = await client.post(API_ENDPOINTS.VITALS.PUSH(id!), {
        encounter_eid,
      })
      setSuccess(`Pushed ${res.data.successful} vitals to OpenEMR (${res.data.failed} failed)`)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to push vitals')
    }
  }
  
  // Generate Vitals dialog state (merged UI)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [job, setJob] = useState<GenerationJob | null>(null)
  
  // Preview state for one-time generation
  const [previewVitals, setPreviewVitals] = useState<any[]>([])
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewSeed, setPreviewSeed] = useState<number | null>(null) // Track the seed used for preview
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false)
  const [forwardUrl, setForwardUrl] = useState('')
  const [forwardVitalId, setForwardVitalId] = useState<number | null>(null)

  // Encounter creation dialog state
  const [encounterDialogOpen, setEncounterDialogOpen] = useState(false)
  const [encounterForm, setEncounterForm] = useState<any>({
    date: new Date().toISOString().slice(0, 10),
    onset_date: '',
    reason: 'Pregnancy Test',
    facility: 'Your Clinic Name Here',
    pc_catid: '5',
    facility_id: '3',
    billing_facility: '3',
    sensitivity: 'normal',
    referral_source: '',
    pos_code: '0',
    external_id: '',
    provider_id: '1',
    class_code: 'AMB',
  })

  // Form state for generation (defaults similar to GenerateVitals)
  const [profile, setProfile] = useState<DiseaseProfile>('normal')
  const [selectedMetrics, setSelectedMetrics] = useState<MetricType[]>(['sbp', 'dbp', 'hr', 'spo2'])
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
  )
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 16))
  const [intervalValue, setIntervalValue] = useState('12h')
  // UI noise range 0-1 (default 0.1)
  const [noiseLevel, setNoiseLevel] = useState(0.1)
  const clamp = (v: number) => Math.max(0, Math.min(1, v))
  const [generateCustomNoise, setGenerateCustomNoise] = useState(false)
  const [generateCustomDropout, setGenerateCustomDropout] = useState(false)
  const [dropoutRate, setDropoutRate] = useState(0.0)
  const [seed, setSeed] = useState<number | ''>('')
  const [dryRun, setDryRun] = useState(false)
  const [destinationUrlGen, setDestinationUrlGen] = useState('')
  const [selectedEncounter, setSelectedEncounter] = useState<string | null>(null)

  const handleMetricToggleGen = (metric: MetricType) => {
    if (selectedMetrics.includes(metric)) {
      setSelectedMetrics(selectedMetrics.filter((m) => m !== metric))
    } else {
      setSelectedMetrics([...selectedMetrics, metric])
    }
  }

  // Generate preview of synthetic vitals
  const generatePreview = async () => {
    if ((selectedMetrics?.length ?? 0) === 0) {
      setPreviewError('Please select at least one metric')
      return
    }

    setPreviewError(null)
    setLoadingPreview(true)

    try {
      const client = request()

      // Use provided seed or generate a random one
      const useSeed = seed !== '' ? seed : Math.floor(Math.random() * 1000000)
      setPreviewSeed(useSeed)

      // map UI noise (0-1) directly to backend and clamp
      const payload: GenerateVitalsRequest = {
        mode: 'profile',
        profile,
        metrics: selectedMetrics,
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        interval: intervalValue,
        noise_level: clamp(noiseLevel),
        dropout_rate: clamp(dropoutRate),
        push_to_openemr: false,
        dry_run: true,
        seed: useSeed,
      }

      const res = await client.post(API_ENDPOINTS.VITALS.PREVIEW, payload)
      setPreviewVitals(res.data.vitals || [])
    } catch (e: any) {
      const detail = e.response?.data?.detail ?? e.response?.data ?? e.message ?? 'Failed to generate preview'
      let message = ''
      if (Array.isArray(detail)) {
        message = detail.map((d: any) => (typeof d === 'string' ? d : d.msg || JSON.stringify(d))).join('; ')
      } else if (typeof detail === 'object') {
        message = detail.msg || JSON.stringify(detail)
      } else {
        message = String(detail)
      }
      setPreviewError(message)
    } finally {
      setLoadingPreview(false)
    }
  }

  // Auto-generate preview when dialog opens or key parameters change
  useEffect(() => {
    if (generateDialogOpen && selectedMetrics.length > 0) {
      generatePreview()
    }
  }, [generateDialogOpen, profile, selectedMetrics, startDate, endDate, intervalValue])

  // regenerate preview when noise settings change
  useEffect(() => {
    if (generateDialogOpen && selectedMetrics.length > 0) {
      generatePreview()
    }
  }, [generateDialogOpen, generateCustomNoise, noiseLevel, generateCustomDropout, dropoutRate])


  const handleGenerate = async () => {
    if (!destinationUrlGen) {
      setGenerateError('Forwarding URL is required')
      return
    }
    if ((selectedMetrics?.length ?? 0) === 0) {
      setGenerateError('Please select at least one metric')
      return
    }
    
    if (!previewSeed) {
      setGenerateError('Please generate a preview first')
      return
    }

    setGenerateError(null)
    setGenerating(true)

    try {
      const client = request()
      const payload: GenerateVitalsRequest = {
        mode: 'profile',
        profile,
        metrics: selectedMetrics,
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        interval: intervalValue,
        dropout_rate: clamp(dropoutRate),
        noise_level: clamp(noiseLevel),
        push_to_openemr: true,
        dry_run: dryRun,
        seed: previewSeed, // Use the exact seed from preview to ensure identical generation
      }

      if (selectedEncounter) {
        // include encounter selection so backend can attach vitals to the chosen encounter
        payload.encounter_eid = selectedEncounter
      }

      const res = await client.post<GenerationJob>(API_ENDPOINTS.VITALS.GENERATE(id!), payload)
      setJob(res.data)
      // If a forwarding URL was provided, call the push endpoint to forward recently generated (unpushed) vitals
      if (destinationUrlGen) {
        try {
          await client.post(API_ENDPOINTS.VITALS.PUSH(id!), { destination_url: destinationUrlGen })
        } catch (e: any) {
          console.debug('Failed to forward vitals to destination URL', e)
        }
      }
      
      // Success: close dialog, refresh vitals, show success message
      setGenerateDialogOpen(false)
      setSuccess('Vitals generated successfully')
      await fetchVitals()
    } catch (e: any) {
      const detail = e.response?.data?.detail ?? e.response?.data ?? e.message ?? 'Failed to generate vitals'
      let message = ''
      if (Array.isArray(detail)) {
        message = detail.map((d: any) => (typeof d === 'string' ? d : d.msg || JSON.stringify(d))).join('; ')
      } else if (typeof detail === 'object') {
        message = detail.msg || JSON.stringify(detail)
      } else {
        message = String(detail)
      }
      setGenerateError(message)
    } finally {
      setGenerating(false)
    }
  }

  useEffect(() => {
    if (generateDialogOpen && (encounters?.length ?? 0) > 0) {
      const first = encounters[0]
      // Prefer UUID fields over integer eid - OpenEMR uses euuid or uuid for encounter identification
      const val = first?.euuid || first?.uuid || (first?.eid ? String(first.eid) : null)
      setSelectedEncounter(val)
    }
  }, [generateDialogOpen, encounters])
  
  const handleCreateEncounter = async () => {
    try {
      const client = request()
      const res = await client.post(API_ENDPOINTS.OPENEMR.CREATE_ENCOUNTER(id!), encounterForm)
      // Parse possible response shapes from backend/OpenEMR
      let createdInfo = 'unknown'
      try {
        const body = res.data || {}
        // If backend returned full OpenEMR response
        if (body.data && typeof body.data === 'object') {
          createdInfo = body.data.uuid || body.data.euuid || body.data.encounter || body.data.eid || JSON.stringify(body.data)
        } else if (body.eid) {
          createdInfo = body.eid
        } else if (body.uuid) {
          createdInfo = body.uuid
        } else {
          createdInfo = JSON.stringify(body)
        }
      } catch (ex) {
        createdInfo = 'unknown'
      }
      setSuccess(`Encounter created: ${createdInfo}`)
      setEncounterDialogOpen(false)
      await fetchPatient()
      await fetchEncounters()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create encounter')
    }
  }


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (!patient) {
    return (
      <Box>
        <Alert severity="error">Patient not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/patients')} sx={{ mt: 1 }}>
          Back to Patients
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ fontSize: '0.9rem' }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/patients')}>
          Back
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {patient.fname} {patient.lname}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            UUID: {patient.uuid}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
          {!hasEncounter && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No encounters found for this patient. Create an encounter before generating or pushing vitals.
            </Alert>
          )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.75} mb={2}>
                {([
                  ['Internal PID', patient.pid || '-'],
                  ['DOB', patient.DOB || '-'],
                  ['Sex', patient.sex || '-'],
                  ['Email', patient.email || '-'],
                  ['Phone (Home)', formatPhone(patient.phone_home)],
                  ['Phone (Cell)', formatPhone(patient.phone_cell)],
                  ['Address', [patient.street, patient.city, patient.state, patient.postal_code].filter(Boolean).join(', ') || '-'],
                ] as [string, string][]).map(([label, value]) => (
                  <Box key={label} display="flex" gap={1}>
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 110, fontWeight: 500 }}>{label}:</Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setGenerateDialogOpen(true)}
                  disabled={!hasEncounter}
                >
                  Generate Vitals (One-Time)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ScheduleIcon />}
                  onClick={() => setScheduleDialogOpen(true)}
                  disabled={!hasEncounter}
                >
                  Create Recurring Schedule
                </Button>
                <Button variant="outlined" startIcon={<PushIcon />} onClick={handlePushVitals} disabled={!hasEncounter}>
                  Push Unpushed Vitals
                </Button>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEncounterDialogOpen(true)}>
                  Create Encounter
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Schedules ({patientSchedules.length})
              </Typography>
              {loadingSchedules ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={20} />
                </Box>
              ) : patientSchedules.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Interval</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Runs</TableCell>
                        <TableCell>Next Run</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patientSchedules.map((schedule) => (
                        <TableRow key={schedule.id} hover onClick={() => navigate(`/schedules/${schedule.id}`)} sx={{ cursor: 'pointer' }}>
                          <TableCell>{schedule.name}</TableCell>
                          <TableCell>{schedule.interval}</TableCell>
                          <TableCell>
                            <Chip 
                              label={schedule.status} 
                              size="small" 
                              color={schedule.status === 'active' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{schedule.run_count || 0}</TableCell>
                          <TableCell>{schedule.next_run_at ? formatLocalDateTime(schedule.next_run_at) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">No schedules created for this patient</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" gutterBottom>
                Vitals ({vitalsTotal})
              </Typography>
              <TextField
                  select
                  size="small"
                  label="Filter Metric"
                  value={filterMetric}
                  onChange={(e) => setFilterMetric(e.target.value as any)}
                  sx={{ width: 220 }}
                >
                  {METRIC_TYPES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label} ({m.unit})
                    </MenuItem>
                  ))}
                </TextField>
                </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(vitals?.length ?? 0) === 0 ? (
                      <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="textSecondary">No vitals generated yet</Typography>
                          </TableCell>
                      </TableRow>
                    ) : (
                      vitals
                        .filter((v) => v.metric === filterMetric)
                        .map((vital) => (
                        <TableRow key={vital.id} sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                            <TableCell>
                              {formatLocalDateTime(vital.timestamp)}
                            </TableCell>
                            <TableCell>
                              {METRIC_TYPES.find((m) => m.value === vital.metric)?.label ||
                                vital.metric}
                            </TableCell>
                            <TableCell align="right">{vital.value.toFixed(1)}</TableCell>
                            <TableCell>{vital.unit}</TableCell>
                            <TableCell>
                              {vital.is_pushed_to_openemr ? (
                                <Chip label="Pushed" size="small" color="success" />
                              ) : vital.push_error ? (
                                <Chip label="Error" size="small" color="error" title={vital.push_error} />
                              ) : (
                                <Chip label="Pending" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => openForwardDialog(vital.id)} title="Forward vital">
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteVital(vital.id)} title="Delete vital">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={1}>
                <Box>
                    <Typography variant="caption" color="textSecondary">
                    Next Push: {formatLocalDateTime(nextRunAt)}
                  </Typography>
                </Box>
                <Box>
                  <Button variant="outlined" color="error" size="small" onClick={handleClearAllVitals}>
                    Clear All Vitals
                  </Button>
                </Box>
              </Box>
              <TablePagination
                component="div"
                count={vitalsTotal}
                page={vitalsPage}
                onPageChange={(_, newPage) => setVitalsPage(newPage)}
                rowsPerPage={vitalsPageSize}
                onRowsPerPageChange={(e) => {
                  setVitalsPageSize(parseInt(e.target.value, 10))
                  setVitalsPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </CardContent>
          </Card>
        </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" gutterBottom>
                      Encounters ({encounters?.length ?? 0})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>eid</TableCell>
                          <TableCell>euuid</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Provider ID</TableCell>
                          <TableCell>Facility</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(encounters?.length ?? 0) === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography color="textSecondary">No encounters available</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          encounters.map((enc: any) => {
                            const encKey = enc.euuid || enc.uuid || enc.eid
                            return (
                              <TableRow key={encKey} sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                                <TableCell>{enc.eid || '-'}</TableCell>
                                <TableCell>{enc.euuid || enc.uuid || '-'}</TableCell>
                                <TableCell>{enc.date || '-'}</TableCell>
                                <TableCell>{enc.reason || '-'}</TableCell>
                                <TableCell>{enc.provider_id || '-'}</TableCell>
                                <TableCell>{enc.facility_name || enc.facility || '-'}</TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
      </Grid>

      {/* Schedule Creation Dialog */}
      <Dialog 
        open={scheduleDialogOpen} 
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Recurring Vitals Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Schedule Name"
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                placeholder="e.g., Daily Morning Vitals"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Disease Profile"
                value={scheduleForm.profile}
                onChange={(e) =>
                  setScheduleForm({ ...scheduleForm, profile: e.target.value as DiseaseProfile })
                }
              >
                {DISEASE_PROFILES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Interval"
                value={scheduleForm.interval}
                onChange={(e) => setScheduleForm({ ...scheduleForm, interval: e.target.value })}
              >
                <MenuItem value="1m">Every 1 minute</MenuItem>
                <MenuItem value="10m">Every 10 minutes</MenuItem>
                <MenuItem value="30m">Every 30 minutes</MenuItem>
                <MenuItem value="1h">Every hour</MenuItem>
                <MenuItem value="6h">Every 6 hours</MenuItem>
                <MenuItem value="12h">Every 12 hours</MenuItem>
                <MenuItem value="1d">Every day</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Window Days"
                value={scheduleForm.window_days}
                onChange={(e) =>
                  setScheduleForm({ ...scheduleForm, window_days: parseInt(e.target.value) || 7 })
                }
                inputProps={{ min: 1, max: 365 }}
                helperText="Days of historical data to generate (sliding window)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Metrics"
                value={scheduleForm.metrics}
                onChange={(e) =>
                  setScheduleForm({ 
                    ...scheduleForm, 
                    metrics: typeof e.target.value === 'string' 
                      ? [e.target.value as MetricType]
                      : e.target.value as MetricType[]
                  })
                }
                SelectProps={{ multiple: true }}
              >
                {METRIC_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label} ({m.unit})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Seed"
                value={scheduleForm.seed}
                onChange={(e) =>
                  setScheduleForm({ ...scheduleForm, seed: parseInt(e.target.value) || 42 })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleCustomNoise}
                    onChange={(e) => {
                      const on = e.target.checked
                      setScheduleCustomNoise(on)
                      if (!on) {
                        setScheduleForm({ ...scheduleForm, noise_level: 0.1 })
                      }
                    }}
                  />
                }
                label="Custom Noise"
              />
              <TextField
                fullWidth
                type="number"
                label="Noise Level"
                value={scheduleForm.noise_level}
                onChange={(e) =>
                  setScheduleForm({ ...scheduleForm, noise_level: parseFloat(e.target.value) || 0.1 })
                }
                inputProps={{ step: 0.05, min: 0, max: 1 }}
                disabled={!scheduleCustomNoise}
                helperText={scheduleCustomNoise ? '0 = no variation, 1 = high variation' : 'Using default noise level (0.1)'}
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleCustomDropout}
                    onChange={(e) => {
                      const on = e.target.checked
                      setScheduleCustomDropout(on)
                      if (!on) {
                        setScheduleForm({ ...scheduleForm, dropout_rate: 0.0 })
                      }
                    }}
                  />
                }
                label="Missing Data Points"
              />
              <TextField
                fullWidth
                type="number"
                label="Dropout Rate"
                value={scheduleForm.dropout_rate}
                onChange={(e) =>
                  setScheduleForm({ ...scheduleForm, dropout_rate: parseFloat(e.target.value) || 0.0 })
                }
                inputProps={{ step: 0.05, min: 0, max: 1 }}
                disabled={!scheduleCustomDropout}
                helperText={scheduleCustomDropout ? '0 = no dropout, 1 = drop all (e.g., 0.2 = drop 20%)' : 'Using default (0.0 = no dropout)'}
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Forwarding URL"
                value={scheduleForm.destination_url}
                onChange={(e) => {
                  setScheduleForm({ ...scheduleForm, destination_url: e.target.value })
                  setScheduleUrlError(null)
                }}
                placeholder="https://your-server.example.com/vitals"
                helperText={scheduleUrlError || "Each generated vital will be forwarded to this URL after OpenEMR ingestion (required)"}
                error={!!scheduleUrlError}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
                  <Button 
                    variant="contained" 
                    onClick={handleCreateSchedule}
                    disabled={!scheduleForm.name || (scheduleForm.metrics?.length ?? 0) === 0 || !scheduleForm.destination_url}
                  >
            Create Schedule
          </Button>
        </DialogActions>
      </Dialog>
      {/* Create Encounter Dialog */}
      <Dialog
        open={encounterDialogOpen}
        onClose={() => setEncounterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Encounter</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={encounterForm.date}
                onChange={(e) => setEncounterForm({ ...encounterForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reason"
                value={encounterForm.reason}
                onChange={(e) => setEncounterForm({ ...encounterForm, reason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Provider ID"
                value={encounterForm.provider_id}
                onChange={(e) => setEncounterForm({ ...encounterForm, provider_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Onset Date"
                type="date"
                value={encounterForm.onset_date}
                onChange={(e) => setEncounterForm({ ...encounterForm, onset_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PC Category ID (pc_catid)"
                value={encounterForm.pc_catid}
                onChange={(e) => setEncounterForm({ ...encounterForm, pc_catid: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Facility ID"
                value={encounterForm.facility_id}
                onChange={(e) => setEncounterForm({ ...encounterForm, facility_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Billing Facility"
                value={encounterForm.billing_facility}
                onChange={(e) => setEncounterForm({ ...encounterForm, billing_facility: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sensitivity"
                value={encounterForm.sensitivity}
                onChange={(e) => setEncounterForm({ ...encounterForm, sensitivity: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Referral Source"
                value={encounterForm.referral_source}
                onChange={(e) => setEncounterForm({ ...encounterForm, referral_source: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="POS Code"
                value={encounterForm.pos_code}
                onChange={(e) => setEncounterForm({ ...encounterForm, pos_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="External ID"
                value={encounterForm.external_id}
                onChange={(e) => setEncounterForm({ ...encounterForm, external_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Facility"
                value={encounterForm.facility}
                onChange={(e) => setEncounterForm({ ...encounterForm, facility: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Class Code"
                value={encounterForm.class_code}
                onChange={(e) => setEncounterForm({ ...encounterForm, class_code: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEncounterDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateEncounter}>Create Encounter</Button>
        </DialogActions>
      </Dialog>
      {/* Forward Vital Dialog */}
      <Dialog
        open={forwardDialogOpen}
        onClose={() => setForwardDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Forward Vital</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Forwarding URL"
            value={forwardUrl}
            onChange={(e) => setForwardUrl(e.target.value)}
            placeholder="https://your-server.example.com/vitals"
            helperText="POST the created vital to this URL after OpenEMR ingestion"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleForwardVital} disabled={!forwardUrl}>
            Forward Vital
          </Button>
        </DialogActions>
      </Dialog>
      {/* Generate Vitals Dialog (merged) */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate Vitals</DialogTitle>
        <DialogContent>
          <Box mb={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Patient: {patient.fname} {patient.lname}
            </Typography>
          </Box>

          {generateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenerateError(null)}>
              {generateError}
            </Alert>
          )}

          {job && (
            <Alert
              severity={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : 'info'}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2">Job #{job.id}</Typography>
              <Typography variant="body2">
                Status: {job.status} | Generated: {job.vitals_generated} | Pushed: {job.vitals_pushed} | Failed:{' '}
                {job.vitals_failed}
              </Typography>
              {job.error_message && (
                <Typography variant="body2" color="error">
                  Error: {job.error_message}
                </Typography>
              )}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Preview Chart Section (moved to top) */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Preview</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={loadingPreview ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={generatePreview}
                      disabled={loadingPreview || (selectedMetrics?.length ?? 0) === 0}
                    >
                      Regenerate
                    </Button>
                  </Box>

                  {previewError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPreviewError(null)}>
                      {previewError}
                    </Alert>
                  )}

                  {loadingPreview ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <CircularProgress />
                    </Box>
                  ) : previewVitals.length > 0 ? (
                    <>
                      <VitalsChart vitals={previewVitals} height={220} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {previewVitals.length} vitals will be generated. Click "Generate Vitals" to submit this exact series.
                      </Typography>
                    </>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <Typography color="text.secondary">Select metrics to generate preview</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Profile & Metrics
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Disease Profile"
                    value={profile}
                    onChange={(e) => setProfile(e.target.value as DiseaseProfile)}
                    sx={{ mb: 1 }}
                  >
                    {DISEASE_PROFILES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Typography variant="subtitle2" gutterBottom>
                    Metrics to Generate
                  </Typography>
                  <FormGroup row>
                    {METRIC_TYPES.map((metric) => (
                      <FormControlLabel
                        key={metric.value}
                        control={
                          <Checkbox
                            checked={selectedMetrics.includes(metric.value)}
                            onChange={() => handleMetricToggleGen(metric.value)}
                          />
                        }
                        label={`${metric.label} (${metric.unit})`}
                      />
                    ))}
                  </FormGroup>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Custom Noise
                  </Typography>
                    <FormControlLabel
                    control={
                      <Switch
                        checked={generateCustomNoise}
                        onChange={(e) => {
                          const on = e.target.checked
                          setGenerateCustomNoise(on)
                          if (!on) setNoiseLevel(0.1)
                        }}
                      />
                    }
                    label="Custom Noise"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Noise Level"
                    value={noiseLevel}
                    onChange={(e) => setNoiseLevel(parseFloat(e.target.value) || 0.1)}
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    helperText={generateCustomNoise ? '0 = no variation, 1 = high variation' : 'Using default noise level (0.1)'}
                    disabled={!generateCustomNoise}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Missing Data Points
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={generateCustomDropout}
                        onChange={(e) => {
                          const on = e.target.checked
                          setGenerateCustomDropout(on)
                          if (!on) setDropoutRate(0.0)
                        }}
                      />
                    }
                    label="Add Missing Data Points"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Dropout Rate"
                    value={dropoutRate}
                    onChange={(e) => setDropoutRate(parseFloat(e.target.value) || 0.0)}
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    helperText={generateCustomDropout ? '0 = no dropout, 1 = drop all (e.g., 0.2 = drop 20%)' : 'Using default (0.0 = no dropout)'}
                    disabled={!generateCustomDropout}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Time Range
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Interval"
                        value={intervalValue}
                        onChange={(e) => setIntervalValue(e.target.value)}
                        helperText="Time between measurements"
                      >
                        <MenuItem value="6h">Every 6 hours</MenuItem>
                        <MenuItem value="12h">Every 12 hours</MenuItem>
                        <MenuItem value="1d">Daily</MenuItem>
                        <MenuItem value="1w">Weekly</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generation Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Random Seed"
                        value={seed}
                        onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : '')}
                        helperText="For reproducible results"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Output Options
                  </Typography>
                  <FormControlLabel
                    control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                    label="Dry Run (don't save)"
                  />

                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Sample external payload (one vital):
                    </Typography>
                    <Box component="pre" sx={{ background: '#f6f8fa', p: 1, mt: 0.5, fontSize: '0.8rem' }}>
                      {`{
  "vital": {
    "id": 123,
    "metric": "sbp",
    "value": 120.0,
    "date": "2026-03-09T12:00:00Z"
  },
  "openemr_response": { "id": 456, "success": true }
}`}
                    </Box>
                  </Box>
                  {(encounters?.length ?? 0) > 0 && (
                    <TextField
                      fullWidth
                      select
                      label="Encounter"
                      value={selectedEncounter || ''}
                      onChange={(e) => setSelectedEncounter(e.target.value)}
                      sx={{ mt: 1 }}
                      helperText="Select which encounter to attach vitals to"
                    >
                      {encounters.map((enc: any) => {
                        // Prefer UUID fields (euuid/uuid) over integer eid
                        const encId = enc.euuid || enc.uuid || (enc.eid ? String(enc.eid) : '')
                        return (
                          <MenuItem key={encId} value={encId}>
                            {`${encId} — ${enc.reason || enc.facility_name || enc.facility || 'No reason'} (${enc.date || 'No date'})`}
                          </MenuItem>
                        )
                      })}
                    </TextField>
                  )}
                  <TextField
                    fullWidth
                    required
                    label="Forwarding URL"
                    value={destinationUrlGen}
                    onChange={(e) => setDestinationUrlGen(e.target.value)}
                    placeholder="https://your-server.example.com/vitals"
                    sx={{ mt: 1 }}
                    helperText="After OpenEMR ingestion, vitals will be POSTed to this URL (required)"
                  />
                </CardContent>
              </Card>
            </Grid>

            

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" mt={1}>
                <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={generating ? <CircularProgress size={20} /> : <AddIcon />}
                  onClick={handleGenerate}
                  disabled={generating || (selectedMetrics?.length ?? 0) === 0 || !previewVitals.length || !destinationUrlGen}
                >
                  {generating ? 'Generating...' : 'Generate Vitals'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default PatientDetail
